import { startCase } from "lodash"
import { dynamicParse, stringFromCamelCase } from "~/utils/tsUtils"
import type { DynamicColType } from "~/components/planning/dynamicTableData/DynamicTableColumns"

export enum ExtraDataTable {
  SKU = "Sku",
  COLLECTION_ON_SKU = "CollectionOnSku",
  INCOMING_ORDER_ITEM = "IncomingOrderItem",
}

const EXTRA_DATA_SUFFIX = "(extradata)"

export function isExtraDataColumnId(columnId: string): boolean {
  return columnId.endsWith(EXTRA_DATA_SUFFIX)
}

export function extraDataFieldNameFromColumnId(columnId: string): string {
  return columnId.endsWith(EXTRA_DATA_SUFFIX)
    ? columnId.slice(0, -EXTRA_DATA_SUFFIX.length)
    : columnId
}

/**
 * Creates the key name with table suffix that matches the format used in extraDataDisplayNameMap
 * @param fieldName - The field name without the (extradata) or (collection-extradata) suffix
 * @param table - The table type (SKU or CollectionOnSku)
 * @returns The key name with table suffix (e.g., "fieldName///Sku")
 */
export function createKeyNameWithTable(fieldName: string, table: ExtraDataTable): string {
  return `${fieldName}///${table}`
}

/**
 * Gets the dynamic column display name for a field
 * @param key - The field name
 * @returns The formatted display name
 */
function getDynamicColumnDisplayName(key: string): string {
  return startCase(stringFromCamelCase(key.replace("extradata", "")))
}

/**
 * Gets the display name for an extra data field using the display name map
 * @param fieldName - The field name without the (extradata) or (collection-extradata) suffix
 * @param table - The table type (SKU or CollectionOnSku)
 * @param displayNameMap - The extra data display name map
 * @returns The display name from the map or fallback to dynamic column display name
 */
export function getExtraDataDisplayName(
  fieldName: string,
  table: ExtraDataTable,
  displayNameMap?: Map<string, string>,
): string {
  const keyNameWithTable = createKeyNameWithTable(fieldName, table)
  return displayNameMap?.get(keyNameWithTable) ?? getDynamicColumnDisplayName(fieldName)
}

/**
 * Gets the aggregation method for an extra data field using the aggregation map
 * @param fieldName - The field name without the (extradata) or (collection-extradata) suffix
 * @param table - The table type (SKU or CollectionOnSku)
 * @param aggregationMap - The extra data aggregation map
 * @param defaultValue - The default aggregation method to return if not found
 * @returns The aggregation method from the map or the default value
 */
export function getExtraDataAggregation(
  fieldName: string,
  table: ExtraDataTable,
  aggregationMap?: Map<string, string>,
  defaultValue: "first" | "sum" | "avg" | "max" | "min" = "first",
): "first" | "sum" | "avg" | "max" | "min" {
  const keyNameWithTable = createKeyNameWithTable(fieldName, table)
  const result = aggregationMap?.get(keyNameWithTable) ?? defaultValue
  // Ensure the result is a valid aggregation type
  if (["first", "sum", "avg", "max", "min"].includes(result)) {
    return result as "first" | "sum" | "avg" | "max" | "min"
  }
  return defaultValue
}

/**
 * Gets the column type for an extra data field using the type map
 * @param fieldName - The field name without the (extradata) or (collection-extradata) suffix
 * @param table - The table type (SKU or CollectionOnSku)
 * @param typeMap - The extra data type map
 * @param defaultValue - The default column type to return if not found
 * @returns The column type from the map or the default value
 */
export function getExtraDataType(
  fieldName: string,
  table: ExtraDataTable,
  typeMap?: Map<string, string>,
  defaultValue: DynamicColType = "string",
): DynamicColType {
  const keyNameWithTable = createKeyNameWithTable(fieldName, table)
  const result = typeMap?.get(keyNameWithTable) ?? defaultValue
  if (["number", "string", "date"].includes(result)) {
    return result as DynamicColType
  }
  return defaultValue
}

/**
 * Parses an extra data value for display, respecting the column's configured type.
 *
 * `dynamicParse` interprets missing/empty values as the number 0, which is what number
 * columns need for aggregation. On string and date columns that turns an absent value
 * into a literal "0", so those keep the value empty instead.
 */
export function parseExtraDataValue(
  value: string | number | null | undefined,
  colType: DynamicColType,
): string | number {
  if (colType !== "number" && (value === null || value === undefined || value === "")) {
    return ""
  }
  return dynamicParse(value ?? "")
}

function isPandasDateTimeType(pandasDataType: string): boolean {
  return pandasDataType.toLowerCase().startsWith("datetime")
}

export function pandasTypeToColType(pandasDataType: string, forceString: boolean): DynamicColType {
  if (forceString) return "string"
  if (pandasDataType === "date" || isPandasDateTimeType(pandasDataType)) return "date"
  if (pandasDataType === "Int64" || pandasDataType === "Float64") return "number"
  return "string"
}
